import type { OpeningHoursSpecification } from 'next-seo/lib/types';

import type { Country } from '@/types/regionTypes';

const makeHrefLangValues = (
  seoHrefLangConfig: {
    [key: string]: string[];
  },
  languageCode: string | undefined,
  supportedLanguages: string[],
  supportedCountryCodes: string[],
) => {
  const notNull = <T>(value: T | null): value is T => value !== null;
  return Object.entries(seoHrefLangConfig).flatMap(([language, countries]) => {
    if (
      (languageCode && language !== languageCode) ||
      !supportedLanguages?.includes(language)
    ) {
      return [];
    }

    return countries
      .map((country) => {
        if (!supportedCountryCodes?.includes(country)) {
          return null;
        }
        return `${language}-${country.toUpperCase()}`;
      })
      .filter(notNull);
  });
};

export const makeHrefLangs = (
  supportedCountries: Country[],
  seoHrefLangConfig: { [key: string]: string[] },
  languageCode?: string,
): string[] => {
  const supportedLanguages = [
    ...Array.from(
      new Set(
        supportedCountries
          .map((country) => country.locales.map((locale) => locale.slug))
          .flat(),
      ),
    ),
  ];

  const supportedCountryCodes = [
    ...Array.from(
      new Set(supportedCountries.map((country) => country.code.toLowerCase())),
    ),
  ];

  if (languageCode && ['zht', 'zh']?.includes(languageCode)) {
    return [
      ...makeHrefLangValues(
        seoHrefLangConfig,
        'zht',
        supportedLanguages,
        supportedCountryCodes,
      ),
      ...makeHrefLangValues(
        seoHrefLangConfig,
        'zh',
        supportedLanguages,
        supportedCountryCodes,
      ),
    ];
  }

  return makeHrefLangValues(
    seoHrefLangConfig,
    languageCode,
    supportedLanguages,
    supportedCountryCodes,
  );
};

export const convertToOpeningHoursSpecification = (data: {
  [key: string]: {
    isClosed?: boolean;
    openIntervals?: {
      end: string;
      start: string;
    }[];
  };
}): OpeningHoursSpecification[] => {
  const specifications: OpeningHoursSpecification[] = [];

  const daysOfWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  for (const day of daysOfWeek) {
    const dayData = data[day];

    if (dayData?.isClosed) {
      continue; // Skip closed days
    }

    if (dayData?.openIntervals) {
      for (const interval of dayData.openIntervals) {
        specifications.push({
          opens: interval.start,
          closes: interval.end,
          dayOfWeek: day.charAt(0).toUpperCase() + day.slice(1), // Capitalize the day
        });
      }
    }
  }

  return specifications;
};
